import type { MeResponse, WorkspaceSummary } from '@nolens/domain';
import { formatDateTime, LOCALE_NAMES, LOCALES, type Locale } from '@nolens/i18n';
import { Avatar, cx, IconButton, Logo, useLocale } from '@nolens/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useId, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { api } from '../lib/api';
import { spaceName, useSetLocale } from '../lib/locale';
import { useNotifications } from '../lib/queries';
import { signOut } from '../lib/session';
import { componentsText } from '../i18n/components';
import { shellText } from '../i18n/shell';
import { GettingStartedWidget } from './GettingStarted';
import {
  ArrowLeftIcon,
  BellIcon,
  CheckIcon,
  ChevronDownIcon,
  ExtensionIcon,
  GuidesIcon,
  HomeIcon,
  LaptopIcon,
  MembersIcon,
  MyGuidesIcon,
  PlusIcon,
  SearchIcon,
  SettingsIcon,
  ShieldIcon,
  SignOutIcon,
  StatsIcon,
  UserIcon,
  UserPlusIcon,
} from './Icons';
import { useShell } from './shellContext';
import { SpaceTile } from './SpaceTile';
import { macVisible } from '../lib/mac';

const label = 'px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-text-3';

function NavItem({
  to,
  icon,
  children,
  count,
  active,
}: {
  to: string;
  icon: ReactNode;
  children: ReactNode;
  count?: number;
  active: boolean;
}) {
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={cx(
        'flex items-center gap-2.5 rounded-[10px] px-2.5 py-[7px] text-[14px] text-text-2 transition hover:bg-surface-2 hover:text-text-1',
        active && 'bg-surface-2 font-semibold text-text-1',
      )}
    >
      <span className={cx('inline-flex shrink-0', active ? 'text-accent-text' : 'text-text-3')}>
        {icon}
      </span>
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {count !== undefined && (
        <span className="text-[12.5px] font-normal text-text-3">{count}</span>
      )}
    </Link>
  );
}

/** Parece um campo de pesquisa; abre a pesquisa em todos os espaços (⌘K / Ctrl+K). */
function SearchButton() {
  const t = shellText(useLocale());
  const { openSearch } = useShell();
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <button
      type="button"
      onClick={openSearch}
      className="flex h-9 w-full items-center gap-2 rounded-[10px] bg-surface-0 px-3 text-[13.5px] text-text-3 shadow-[inset_0_0_0_1px_var(--border)] transition hover:text-text-2 hover:shadow-[inset_0_0_0_1px_var(--border-strong)]"
      data-open-search
    >
      <SearchIcon size={16} />
      <span className="flex-1 text-left">{t.searchGuides}</span>
      <kbd className="rounded-[5px] bg-surface-2 px-1.5 font-sans text-[11px] text-text-3">
        {mac ? '⌘K' : 'Ctrl K'}
      </kbd>
    </button>
  );
}

/** O espaço em contexto: troca de espaço (abre a lista no sítio) e as páginas dele. */
function SpaceSection({ me, ws }: { me: MeResponse; ws: WorkspaceSummary }) {
  const locale = useLocale();
  const t = shellText(locale);
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const base = `/w/${ws.id}`;
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        title={t.switchSpace}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2.5 rounded-[10px] px-2 py-1.5 text-left transition hover:bg-surface-2"
        data-space-switcher
      >
        <SpaceTile ws={ws} size={26} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-semibold">{spaceName(ws, locale)}</span>
          <span className="block text-[11.5px] text-text-3">
            {ws.kind === 'team' ? t.team : t.personalSpace}
          </span>
        </span>
        <ChevronDownIcon size={16} className={cx('text-text-3 transition', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="mt-1 rounded-[12px] bg-surface-0 p-1 shadow-[inset_0_0_0_1px_var(--border)]">
          <p className={label}>{t.spaces}</p>
          <ul className="grid gap-0.5">
            {me.workspaces.map((w) => (
              <li key={w.id}>
                <Link
                  to={`/w/${w.id}`}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2.5 rounded-[8px] px-2 py-1.5 text-[13.5px] hover:bg-surface-2"
                >
                  <SpaceTile ws={w} size={20} />
                  <span className="min-w-0 flex-1 truncate">{spaceName(w, locale)}</span>
                  {w.id === ws.id ? (
                    <CheckIcon size={15} className="text-accent-text" />
                  ) : (
                    <span className="text-[12px] text-text-3">{w.guides}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
          <Link
            to="/equipas/nova"
            className="mt-0.5 flex items-center gap-2.5 rounded-[8px] px-2 py-1.5 text-[13.5px] text-text-2 hover:bg-surface-2 hover:text-text-1"
          >
            <PlusIcon size={16} className="mx-0.5" />
            {t.newTeam}
          </Link>
        </div>
      )}
      <nav aria-label={spaceName(ws, locale)} className="mt-1 grid gap-0.5">
        <NavItem
          to={base}
          icon={<GuidesIcon />}
          count={ws.guides}
          active={pathname === base || pathname.startsWith('/g/')}
        >
          {t.guides}
        </NavItem>
        <NavItem
          to={`${base}/estatisticas`}
          icon={<StatsIcon />}
          active={
            pathname.startsWith(`${base}/estatisticas`) || pathname.startsWith(`${base}/poupanca`)
          }
        >
          {t.stats}
        </NavItem>
        {ws.kind === 'team' && (
          <NavItem
            to={`${base}/membros`}
            icon={<MembersIcon />}
            count={ws.members}
            active={pathname.startsWith(`${base}/membros`)}
          >
            {t.members}
          </NavItem>
        )}
        <NavItem
          to={`${base}/definicoes`}
          icon={<SettingsIcon />}
          active={pathname.startsWith(`${base}/definicoes`) || pathname.startsWith(`${base}/marca`)}
        >
          {t.settings}
        </NavItem>
      </nav>
    </div>
  );
}

/** Rodapé: a conta (com sair), a língua e a extensão. */
function AccountFooter({ me }: { me: MeResponse }) {
  const locale = useLocale();
  const t = shellText(locale);
  const email = me.user.email ?? '';
  return (
    <div className="shrink-0 border-t border-border p-3">
      <div className="flex items-center gap-1">
        <Link
          to="/conta"
          title={t.account}
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-[10px] px-2 py-1.5 hover:bg-surface-2"
          data-account-link
        >
          <Avatar name={email || '?'} size={28} />
          <span className="min-w-0 truncate text-[13.5px] font-medium">{email}</span>
        </Link>
        <IconButton label={t.signOut} onClick={() => void signOut()}>
          <SignOutIcon size={17} />
        </IconButton>
      </div>
      <div className="mt-1 flex items-center justify-between gap-1 px-1">
        <LanguageSelect value={locale} />
        <div className="flex items-center">
          {/* A app de Mac (ADR-G25): só o proprietário, ou quem já a ligou */}
          {macVisible(me) && (
            <Link
              to="/mac"
              aria-label={t.macApp}
              title={t.macApp}
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-text-2 hover:bg-surface-2 hover:text-text-1"
            >
              <LaptopIcon size={16} />
            </Link>
          )}
          <Link
            to="/extensao"
            aria-label={t.extension}
            title={t.extension}
            className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-text-2 hover:bg-surface-2 hover:text-text-1"
          >
            <ExtensionIcon size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Barra lateral da app: logótipo e avisos, pesquisa, Início e os guias da pessoa, o espaço
 * em contexto (com as páginas dele), convidar ou criar equipa, primeiros passos e a conta.
 */
export function Sidebar({ me, ws, bell }: { me: MeResponse; ws: WorkspaceSummary; bell: boolean }) {
  const t = shellText(useLocale());
  const { pathname } = useLocation();
  const inviteTo = ws.kind === 'team' && ws.role === 'owner' ? `/w/${ws.id}/membros` : null;
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-14 shrink-0 items-center justify-between gap-2 pl-4 pr-2">
        <Link to="/inicio" aria-label={t.home} className="whitespace-nowrap">
          <Logo />
        </Link>
        {bell && <Bell align="left" />}
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pb-3 [&>*]:shrink-0">
        <SearchButton />
        <nav aria-label={t.sidebar} className="mt-3 grid gap-0.5">
          <NavItem to="/inicio" icon={<HomeIcon />} active={pathname === '/inicio'}>
            {t.home}
          </NavItem>
          <NavItem to="/guias" icon={<MyGuidesIcon />} active={pathname === '/guias'}>
            {t.myGuides}
          </NavItem>
          {me.isPlatformAdmin && (
            <NavItem to="/admin" icon={<ShieldIcon />} active={pathname === '/admin'}>
              {t.admin}
            </NavItem>
          )}
        </nav>
        <div className="my-3 border-t border-border" />
        <SpaceSection me={me} ws={ws} />
        <div className="min-h-6 flex-1" />
        <div className="grid gap-1">
          <Link
            to={inviteTo ?? '/equipas/nova'}
            className="flex h-9 items-center justify-center gap-2 rounded-full text-[13.5px] font-medium shadow-[inset_0_0_0_1px_var(--border-strong)] transition hover:bg-surface-2"
          >
            {inviteTo ? <UserPlusIcon size={16} /> : <PlusIcon size={16} />}
            {inviteTo ? t.invite : t.createTeam}
          </Link>
          <GettingStartedWidget me={me} />
        </div>
      </div>
      <AccountFooter me={me} />
    </div>
  );
}

/** Barra lateral das definições: voltar, os espaços (as definições de cada um) e a conta. */
export function SettingsSidebar({ me, ws }: { me: MeResponse; ws: WorkspaceSummary }) {
  const locale = useLocale();
  const t = shellText(locale);
  const { pathname } = useLocation();
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-14 shrink-0 items-center gap-1 px-2">
        <Link
          to={`/w/${ws.id}`}
          aria-label={t.backFromSettings}
          title={t.backFromSettings}
          className="inline-flex size-9 items-center justify-center rounded-full text-text-2 hover:bg-surface-2 hover:text-text-1"
        >
          <ArrowLeftIcon />
        </Link>
        <span className="text-[15px] font-semibold">{t.settings}</span>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pb-3 [&>*]:shrink-0">
        <div className="flex items-center justify-between">
          <p className={label}>{t.spaces}</p>
          <Link
            to="/equipas/nova"
            aria-label={t.newTeam}
            title={t.newTeam}
            className="inline-flex size-7 items-center justify-center rounded-full text-text-3 hover:bg-surface-2 hover:text-text-1"
          >
            <PlusIcon size={16} />
          </Link>
        </div>
        <nav aria-label={t.spaces} className="grid gap-0.5">
          {me.workspaces.map((w) => (
            <NavItem
              key={w.id}
              to={`/w/${w.id}/definicoes`}
              icon={<SpaceTile ws={w} size={20} />}
              active={pathname.startsWith(`/w/${w.id}/`)}
            >
              {spaceName(w, locale)}
            </NavItem>
          ))}
        </nav>
        <p className={cx(label, 'mt-3')}>{t.yourAccount}</p>
        <nav aria-label={t.yourAccount} className="grid gap-0.5">
          <NavItem to="/conta" icon={<UserIcon />} active={pathname === '/conta'}>
            {t.account}
          </NavItem>
          <NavItem to="/extensao" icon={<ExtensionIcon />} active={pathname === '/extensao'}>
            {t.extension}
          </NavItem>
          {macVisible(me) && (
            <NavItem to="/mac" icon={<LaptopIcon />} active={pathname.startsWith('/mac')}>
              {t.macApp}
            </NavItem>
          )}
        </nav>
      </div>
      <AccountFooter me={me} />
    </div>
  );
}

/** Avisos dos guias que segue (alterações e comentários). */
export function Bell({ align }: { align: 'left' | 'right' }) {
  const locale = useLocale();
  const t = componentsText(locale);
  const id = useId();
  const q = useNotifications();
  const qc = useQueryClient();
  const list = q.data ?? [];
  const unread = list.filter((n) => !n.read).length;
  const markRead = () => {
    if (!unread) return;
    void api('/v1/notifications/read', { method: 'POST' }).then(() =>
      qc.invalidateQueries({ queryKey: ['notifications'] }),
    );
  };
  return (
    <div className="relative">
      <button
        type="button"
        popoverTarget={id}
        aria-label={unread ? t.notificationsUnread(unread) : t.notifications}
        className="relative inline-flex size-9 items-center justify-center rounded-full text-text-2 hover:bg-surface-2 hover:text-text-1"
        onClick={markRead}
      >
        <BellIcon />
        {unread > 0 && (
          <span
            className="absolute right-1 top-1 inline-flex min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10.5px] font-semibold text-on-accent"
            data-unread={unread}
          >
            {unread}
          </span>
        )}
      </button>
      <div
        id={id}
        popover="auto"
        className={cx(
          'fixed inset-auto top-14 m-0 w-[min(20rem,calc(100vw-2rem))] rounded-[14px] bg-surface-1 p-2 shadow-[0_12px_32px_rgba(0,0,0,.16),inset_0_0_0_1px_var(--border)]',
          align === 'left' ? 'left-4' : 'right-4',
        )}
      >
        {list.length === 0 ? (
          <p className="p-3 text-sm text-text-2">{t.noNotifications}</p>
        ) : (
          <ul className="max-h-96 overflow-y-auto">
            {list.map((n) => (
              <li key={n.id}>
                <Link
                  to={`/g/${n.guideId}`}
                  className={cx(
                    'block rounded-[10px] px-3 py-2 text-[13.5px] hover:bg-surface-2',
                    !n.read && 'font-medium',
                  )}
                >
                  {t.notified(
                    n.actorEmail ?? t.someone,
                    n.kind === 'comment' ? 'comment' : 'update',
                    n.guideTitle || t.untitled,
                  )}
                  <span className="block text-[12px] font-normal text-text-3">
                    {formatDateTime(locale, n.createdAt)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Língua da interface (fica na conta; a extensão grava os guias novos nesta língua). */
function LanguageSelect({ value }: { value: Locale }) {
  const set = useSetLocale();
  return (
    // Nas duas línguas de propósito: quem não percebe a atual tem de encontrar o seletor.
    <label
      className="relative inline-flex min-w-0 items-center text-text-2"
      // i18n-ignore
      title="Idioma / Language"
    >
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        className="pointer-events-none absolute left-2"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
      </svg>
      <select
        // i18n-ignore
        aria-label="Idioma / Language"
        className="h-8 min-w-0 max-w-full appearance-none truncate rounded-full bg-transparent pl-7 pr-2 text-[13px] hover:bg-surface-2"
        value={value}
        disabled={set.isPending}
        onChange={(e) => set.mutate(e.target.value as Locale)}
        data-locale-select
      >
        {LOCALES.map((l) => (
          <option key={l} value={l}>
            {LOCALE_NAMES[l]}
          </option>
        ))}
      </select>
    </label>
  );
}
